import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select } from "antd";
import { useTranslation } from "react-i18next";
import axios, { AxiosError, AxiosResponse } from "axios";
import * as Yup from "yup";
import { IContractor } from "../index";
import FormItem from "../../../components/FormItem";
import filterOptions from "../../../utils/filterOptions";
import { ICustomer } from "../../Customers";
import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";
import getVaidationErrors from "../../../utils/getValidationErrors";

interface IContractorModalProps {
  visible: boolean;
  handleCloseModal: (contractor?: IContractor) => void;
  selectedContractor?: IContractor;
}

interface ISubmitData {
  name: string;
  phone_number?: string;
  email: string;
  customer_id: string;
}

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const ContractorModal: FC<IContractorModalProps> = ({
  handleCloseModal,
  visible,
  selectedContractor,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [customers, setCustomers] = useState<ICustomer[]>([]);

  const { t } = useTranslation(["customers"]);
  const { addToast } = useToast();

  const [form] = Form.useForm();

  useEffect(() => {
    setLoadingCustomers(true);
    api
      .get<ICustomer[]>("/customers")
      .then(response => {
        setCustomers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description: t("request_status.default"),
        });
      })
      .finally(() => setLoadingCustomers(false));
  }, [addToast, t]);

  useEffect(() => {
    if (selectedContractor) {
      form.setFieldsValue({
        ...selectedContractor,
      });
    } else {
      form.resetFields();
    }
    setValidationErrors({});
  }, [selectedContractor, form]);

  const customersOptions = useMemo(() => {
    if (!customers) {
      return undefined;
    }

    return customers
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(customer => ({
        label: customer.name,
        value: customer.id,
      }));
  }, [customers]);

  const handleClose = useCallback(
    (cust?: IContractor) => {
      setValidationErrors({});
      form.resetFields();
      handleCloseModal(cust);
    },
    [form, handleCloseModal],
  );

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 403:
          description = t("contractors.request_status.403");
          break;
        case 404:
          description = t("contractors.request_status.404");
          break;
        default:
          description = t("contractors.request_status.default");
      }

      addToast({
        type: "error",
        title: "Erro na criação",
        description,
      });
    },
    [addToast],
  );

  const handleRequest = useCallback(
    async (payload: ISubmitData) => {
      setLoading(true);
      try {
        let response: AxiosResponse<IContractor>;
        if (!selectedContractor) {
          response = await api.post("/contractors", payload);
        } else {
          response = await api.patch(
            `/contractors/${selectedContractor.id}`,
            payload,
          );
        }
        addToast({
          type: "success",
          title: "Sucesso",
          description: "O contratante foi criado com sucesso",
        });

        handleClose(response.data);
      } catch (err) {
        addToast({
          type: "error",
          title: "Erro na criação",
          description: t("contractors.request_status.default"),
        });
      }
      setLoading(false);
    },
    [addToast, handleCloseModal, selectedContractor],
  );

  const handleSubmit = useCallback(
    async (data: ISubmitData) => {
      try {
        const schema = Yup.object().shape({
          name: Yup.string().required("Nome obrigatório"),
          email: Yup.string()
            .required("Email obrigatório")
            .email("Digite um email válido"),
          customer_id: Yup.string().required("Cliente obrigatório"),
        });
        await schema.validate(data, { abortEarly: false });
        await handleRequest(data);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getVaidationErrors(err);
          setValidationErrors(errors);
          return;
        }
        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
        }
      }
    },
    [generateErrorToast, handleRequest],
  );
  return (
    <Modal
      title={
        !selectedContractor
          ? t("contractors.actions.create")
          : t("contractors.actions.edit")
      }
      onOk={() => form.submit()}
      visible={visible}
      onCancel={() => handleClose()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      destroyOnClose
      forceRender
    >
      <Form
        {...layout}
        form={form}
        name="control-hooks"
        onFinish={handleSubmit}
      >
        <FormItem
          name="customer_id"
          label={t("contractors.customer")}
          required
          errors={validationErrors}
        >
          <Select
            allowClear
            showSearch
            filterOption={filterOptions}
            options={customersOptions}
            loading={loadingCustomers}
          />
        </FormItem>
        <FormItem
          name="name"
          required
          label={t("contractors.name")}
          errors={validationErrors}
        >
          <Input />
        </FormItem>
        <FormItem
          name="email"
          required
          label={t("contractors.email")}
          errors={validationErrors}
        >
          <Input />
        </FormItem>
        <FormItem
          name="phone_number"
          label={t("contractors.phone_number")}
          errors={validationErrors}
        >
          <Input />
        </FormItem>
      </Form>
    </Modal>
  );
};

export default ContractorModal;
